import { defineComponent as _defineComponent } from 'vue'
import VsText from '@/components/vision/VsText.vue'
import { computed, reactive, ref } from 'vue'
import { useActions, useGetters, useStore } from 'vuex-composition-helpers'
import Job, { ApprovalStatus, JobStatus } from '@/models/job'
import Company from '@/models/company'
import { saveFile } from '@/utils/save-file'
import {
  getJobPdf,
  useBulkArchiveMutation,
  useUnarchiveMutation,
} from '@/api/jobs'
import { SnackType } from '@/components/vision/SnackBarType'
import VsButton from '@/components/vision/VsButton.vue'
import { useRoute, useRouter } from 'vue-router/composables'
import VsMenu from '@/components/vision/VsMenu.vue'
import { useRemoveJobsFromInvoiceMutation } from '@/api/invoice'
import { useBusinessUnitPermissions } from '@/components/tickets/common/useBusinessUnitPermissions'

type MenuItem = {
  text: string
  icon: string
  onClick: () => void
}

interface Props {
  jobModel: Job
  company: Company | undefined
  parentRouteName: string
}


export default /*#__PURE__*/_defineComponent({
  __name: 'ReadonlyJobFormActionBar',
  props: {
    jobModel: null,
    company: null,
    parentRouteName: null
  },
  emits: ["refresh-job-history", "handle-snackbar", "open-email-dialog", "update-readonly-job", "open-approval-rejection-bottom-sheet", "open-add-to-batch-bottom-sheet", "open-send-for-approval-bottom-sheet"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'refresh-job-history')
  (
    e: 'handle-snackbar',
    value: {
      type?: SnackType
      messageKey?: string | null
      customMessage?: string
    }
  )
  (e: 'open-email-dialog')
  (e: 'update-readonly-job')
  (e: 'open-approval-rejection-bottom-sheet')
  (e: 'open-add-to-batch-bottom-sheet')
  (e: 'open-send-for-approval-bottom-sheet')
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  jobModel: Job
  company: Company | undefined
  parentRouteName: string
}

const route = useRoute()
const router = useRouter()





const isApproving = ref(false)
const isSubmittingForApproval = ref(false)
const isPdfDownloading = ref(false)

const menuItems = computed(() => {
  const items: MenuItem[] = []
  if (jobIsApproved.value && canUnapprove.value) {
    items.push({
      text: 'Unapprove',
      icon: 'mdi-lock-open-outline',
      onClick: unapprove,
    })
  }
  if (canArchiveJob.value(job.value.hubId, job.value.accountId)) {
    items.push({
      text: archiveBtnLabel.value,
      icon: archiveBtnIcon.value,
      onClick: archiveBtnClicked,
    })
  }
  if (
    job.value.invoiceId &&
    canEditBatch.value(job.value.serviceProviderBusinessUnitId)
  ) {
    items.push({
      text: 'Remove from batch',
      icon: 'mdi-link-variant-off',
      onClick: removeFromBatch,
    })
  }
  if (
    job.value.invoiceId &&
    canViewBatch.value(job.value.serviceProviderBusinessUnitId)
  ) {
    items.push({
      text: 'View batch',
      icon: 'mdi-wallet-bifold-outline',
      onClick: viewBatch,
    })
  }
  if (showSecondaryAddToBatchAction.value) {
    items.push({
      text: 'Add to batch',
      icon: 'mdi-wallet-bifold-outline',
      onClick: openAddToBatchBottomSheet,
    })
  }
  if (showSecondaryApproveAction.value) {
    items.push({
      text: 'Approve',
      icon: 'mdi-check-all',
      onClick: approve,
    })
  }
  if (showCancelApprovalRequestAction.value) {
    items.push({
      text: 'Cancel approval request',
      icon: 'mdi-undo',
      onClick: cancelApprovalRequest,
    })
  }
  if (showUnCompleteJobRequestAction.value) {
    items.push({
      text: 'Reopen',
      icon: 'mdi-backup-restore',
      onClick: unCompleteJobRequest,
    })
  }

  return items
})

const { submitJobForApprovalEnabled } = useGetters('featureFlags', [
  'submitJobForApprovalEnabled',
])
const {
  canApproveJob,
  canSubmitForApproval,
  canArchiveJob,
  canUnapproveJob,
  canViewBatch,
  canViewAddToBatchSelectionPage,
} = useGetters('permissions', [
  'canApproveJob',
  'canSubmitForApproval',
  'canArchiveJob',
  'canUnapproveJob',
  'canViewBatch',
  'canViewAddToBatchSelectionPage',
])
const {
  approveJob,
  submitJobForApproval,
  cancelJobApprovalRequest,
  unCompleteJob,
  unapproveJob,
} = useActions('jobs', [
  'approveJob',
  'submitJobForApproval',
  'cancelJobApprovalRequest',
  'unCompleteJob',
  'unapproveJob',
])

const canReopenJob = computed<(hubId: string, jobAccountId: string) => boolean>(
  () => store.getters['permissions/canReopenJob']
)

const store = useStore()
const canEditBatch = computed<(batchOwnerId: string | null) => boolean>(
  () => store.getters['permissions/canEditBatch']
)
const ticketErrors = computed(() => store.getters['tickets/getError'])

const job = computed(() => reactive(props.jobModel))
const hasPdfs = computed(() => pdfFormats.value.length > 0)
const pdfFormats = computed(() =>
  (
    props.company?.pdfFormats?.filter((p) =>
      job.value.activity?.pdfFormatIds.includes(p.id)
    ) ?? []
  ).sort((a, b) => (a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1))
)

const jobIsApproved = computed(() => job.value.isApproved)
const showApproveAction = computed(
  (): boolean =>
    job.value.isPendingApproval &&
    canApproveJob.value(job.value.hubId, job.value.accountId)
)
const showSecondaryApproveAction = computed(
  () =>
    !job.value.isPendingApproval &&
    !job.value.isApproved &&
    job.value.status === JobStatus.COMPLETED &&
    submitJobForApprovalEnabled.value &&
    canApproveJob.value(job.value.hubId, job.value.accountId)
)
const showSubmitForApproveAction = computed(
  (): boolean =>
    submitJobForApprovalEnabled.value &&
    job.value.isCompleted &&
    !(job.value.isPendingApproval || job.value.isApproved) &&
    canSubmitForApproval.value(job.value.serviceProviderBusinessUnitId)
)
const { canSendEmailForApproval } = useBusinessUnitPermissions()
const showSendEmailForApproveAction = computed(
  () => showSubmitForApproveAction.value && canSendEmailForApproval.value
)
const showCancelApprovalRequestAction = computed(
  (): boolean =>
    submitJobForApprovalEnabled.value &&
    job.value.isCompleted &&
    job.value.isPendingApproval &&
    canSubmitForApproval.value(job.value.serviceProviderBusinessUnitId)
)
const showAddToBatchAction = computed(
  (): boolean =>
    job.value.invoiceId === null && canViewAddToBatchSelectionPage.value
)
const showSecondaryAddToPdf = computed(
  () =>
    (showSubmitForApproveAction.value ||
      showApproveAction.value ||
      showAddToBatchAction.value) &&
    hasPdfs.value
)
const showSecondaryAddToBatchAction = computed(
  () =>
    (showSubmitForApproveAction.value || showApproveAction.value) &&
    showAddToBatchAction.value
)
const showUnCompleteJobRequestAction = computed(
  () =>
    job.value.status === JobStatus.COMPLETED &&
    job.value.approvalStatus !== ApprovalStatus.APPROVED &&
    !job.value.isArchived &&
    canReopenJob.value(job.value.hubId, job.value.accountId)
)
const canUnapprove = computed(
  () =>
    jobIsApproved.value &&
    canUnapproveJob.value(
      job.value.hubId,
      job.value.approvedBy,
      job.value.accountId
    )
)

const archiveBtnIcon = computed(() =>
  job.value.isArchived
    ? 'mdi-archive-arrow-up-outline'
    : 'mdi-archive-arrow-down-outline'
)
const archiveBtnLabel = computed(() =>
  job.value.isArchived ? 'Unarchive' : 'Archive'
)

async function submitForApproval() {
  isSubmittingForApproval.value = true
  try {
    await submitJobForApproval({ uuid: job.value.uuid })
    emit('update-readonly-job')
    emit('refresh-job-history')
    emit('handle-snackbar', {
      type: SnackType.INFO,
      messageKey: 'submittedForApproval',
    })
  } catch (error) {
    emit('handle-snackbar', {
      type: SnackType.ERROR,
      messageKey: 'submitForApprovalFailed',
    })
  } finally {
    isSubmittingForApproval.value = false
  }
}

async function cancelApprovalRequest() {
  try {
    await cancelJobApprovalRequest({ uuid: job.value.uuid })
    emit('update-readonly-job')
    emit('refresh-job-history')
    emit('handle-snackbar', {
      type: SnackType.INFO,
      messageKey: 'cancelApprovalRequest',
    })
  } catch (error) {
    emit('handle-snackbar', {
      type: SnackType.ERROR,
      messageKey: 'cancelApprovalRequestFailed',
    })
  }
}

async function unCompleteJobRequest() {
  await unCompleteJob({ jobNumber: job.value.id })
  if (!ticketErrors.value) {
    emit('update-readonly-job')
    emit('refresh-job-history')
    emit('handle-snackbar', {
      type: SnackType.INFO,
      messageKey: 'unCompleteJobRequest',
    })
  } else {
    emit('handle-snackbar', {
      type: SnackType.ERROR,
      messageKey: 'unCompleteJobRequestFailed',
    })
  }
}

async function approve() {
  isApproving.value = true
  try {
    await approveJob({ id: job.value.id })
    emit('update-readonly-job')
    emit('refresh-job-history')
    emit('handle-snackbar', { type: SnackType.INFO, messageKey: 'approved' })
  } catch (error) {
    emit('handle-snackbar', {
      type: SnackType.ERROR,
      messageKey: 'approvalFailed',
    })
  } finally {
    isApproving.value = false
  }
}

function openRejectionSheet() {
  emit('open-approval-rejection-bottom-sheet')
}

async function downloadPdf(pdfFormatId: string) {
  isPdfDownloading.value = true

  try {
    saveFile(await getJobPdf(job.value.uuid, pdfFormatId))
  } catch {
    emit('handle-snackbar', {
      type: SnackType.ERROR,
      messageKey: 'pdfDownloadError',
    })
  }

  isPdfDownloading.value = false
}

function choosePDFFormat(openPdfMenu: () => void) {
  if (pdfFormats.value.length === 1) {
    downloadPdf(pdfFormats.value[0].id)
  } else {
    openPdfMenu()
  }
}

async function unapprove() {
  isApproving.value = true
  try {
    await unapproveJob({ id: job.value.id })
    emit('update-readonly-job')
    emit('refresh-job-history')
    emit('handle-snackbar', { type: SnackType.INFO, messageKey: 'unapproved' })
  } catch (error) {
    emit('handle-snackbar', {
      type: SnackType.ERROR,
      messageKey: 'unapprovalFailed',
    })
  } finally {
    isApproving.value = false
  }
}

const { mutateAsync: bulkArchiveAsync } = useBulkArchiveMutation()
const { mutateAsync: unarchiveAsync } = useUnarchiveMutation()

async function archiveBtnClicked() {
  const successKey = job.value.isArchived ? 'unarchived' : 'archived'
  const errorKey = job.value.isArchived ? 'unarchiveFailed' : 'archiveFailed'

  try {
    if (job.value.isArchived) {
      await unarchiveAsync({ jobId: job.value.uuid })
    } else {
      await bulkArchiveAsync({ jobIds: [job.value.uuid] })
    }
    emit('update-readonly-job')
    emit('refresh-job-history')
    emit('handle-snackbar', { type: SnackType.INFO, messageKey: successKey })
  } catch (error) {
    if (error.response?.data?.detail) {
      emit('handle-snackbar', {
        customMessage: error.response.data.detail.toString(),
        type: SnackType.ERROR,
      })
    } else {
      emit('handle-snackbar', { messageKey: errorKey, type: SnackType.ERROR })
    }
  }
}

function clone() {
  router.push({
    name: route.name ?? undefined,
    params: { id: 'new', hubId: job.value.hubId },
    query: { fromJob: job.value.id.toString() },
  })
}

const { mutateAsync: removeJobsFromBatch } = useRemoveJobsFromInvoiceMutation()
const removeFromBatch = async () => {
  if (!job.value.invoiceId) return
  try {
    await removeJobsFromBatch({
      id: job.value.invoiceId ?? '',
      jobIds: [job.value.uuid],
    })
    emit('update-readonly-job')
    emit('refresh-job-history')
    emit('handle-snackbar', {
      type: SnackType.INFO,
      messageKey: 'removedJobFromBatch',
    })
  } catch (e) {
    emit('handle-snackbar', {
      messageKey: 'removedJobFromBatchFailure',
      type: SnackType.ERROR,
    })
  }
}

const viewBatch = async () => {
  if (!job.value.invoiceId) return
  await router.push({
    name: 'job-search-batches-info-panel',
    params: {
      batchId: job.value.invoiceId,
    },
  })
}

function openAddToBatchBottomSheet() {
  emit('open-add-to-batch-bottom-sheet')
}

return { __sfc: true,route, router, props, emit, isApproving, isSubmittingForApproval, isPdfDownloading, menuItems, submitJobForApprovalEnabled, canApproveJob, canSubmitForApproval, canArchiveJob, canUnapproveJob, canViewBatch, canViewAddToBatchSelectionPage, approveJob, submitJobForApproval, cancelJobApprovalRequest, unCompleteJob, unapproveJob, canReopenJob, store, canEditBatch, ticketErrors, job, hasPdfs, pdfFormats, jobIsApproved, showApproveAction, showSecondaryApproveAction, showSubmitForApproveAction, canSendEmailForApproval, showSendEmailForApproveAction, showCancelApprovalRequestAction, showAddToBatchAction, showSecondaryAddToPdf, showSecondaryAddToBatchAction, showUnCompleteJobRequestAction, canUnapprove, archiveBtnIcon, archiveBtnLabel, submitForApproval, cancelApprovalRequest, unCompleteJobRequest, approve, openRejectionSheet, downloadPdf, choosePDFFormat, unapprove, bulkArchiveAsync, unarchiveAsync, archiveBtnClicked, clone, removeJobsFromBatch, removeFromBatch, viewBatch, openAddToBatchBottomSheet, VsText, VsButton, VsMenu }
}

})